import { useEffect, useMemo, useState } from 'react';
import { EReportingStatus } from 'constants/reporting.constants';
import dayjs from 'dayjs';
import { useQueryParam } from 'hooks/useQuery';
import {
  fetchTemplate,
  reportTemplatesSelector,
} from 'slices/reportTemplate.slice';
import {
  fetchReportingLogs,
  reportingLogsSelector,
  totalResultsSelector,
} from 'slices/reportingLogs.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TReportingLog } from 'types/reporting.type';
import {
  DefaultContentLayout,
  EditableTable,
  Tag,
} from '@timeedit/ui-components';
import {
  type PaginationProps,
  Select,
  Tabs,
} from '@timeedit/ui-components/antd';
import LogItemTable from './components/LogItemTable';
import RenderIcon from './components/RenderIcon';
import './index.scss';

const ReportingLogs = () => {
  const dispatch = useAppDispatch();
  const logs = useAppSelector(reportingLogsSelector);
  const totalResults = useAppSelector(totalResultsSelector);
  const templates = useAppSelector(reportTemplatesSelector);

  const [curPage, setCurPage] = useState(1);
  const [status, setStatus] = useState<EReportingStatus | null>();
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);

  const queryTemplateId = useQueryParam('templateId');

  useEffect(() => {
    dispatch(fetchTemplate());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchReportingLogs({
        page: curPage,
        status,
        templateId: activeTemplate,
      })
    );
  }, [curPage, status, activeTemplate]);

  useEffect(() => {
    if (queryTemplateId) {
      setActiveTemplate(queryTemplateId);
      return;
    }

    if (templates[0]) {
      setActiveTemplate(templates[0].id);
    }
  }, [templates, queryTemplateId]);

  const pagination: PaginationProps = {
    current: curPage,
    onChange: setCurPage,
    total: totalResults,
    pageSize: 10,
    size: 'small',
  };

  const tabItems = useMemo(
    () =>
      templates.map((template) => ({
        key: template.id,
        label: template.name,
      })),
    [templates]
  );

  return (
    <DefaultContentLayout title="Reporting Logs">
      <div className="reporting-logs">
        <Tabs
          type="card"
          items={tabItems}
          activeKey={activeTemplate}
          onChange={(key) =>
            setActiveTemplate((prev) => {
              if (prev !== key) {
                setCurPage(1);
              }
              return key;
            })
          }
        />
        <div className="filter">
          <Select
            onChange={setStatus}
            value={status}
            options={Object.keys(EReportingStatus).map((st) => ({
              value: st,
              label: st,
            }))}
            allowClear
            placeholder="Filter by status"
            popupMatchSelectWidth={false}
            size="small"
          />
        </div>
        <EditableTable
          rowKey={'id'}
          columns={columns}
          dataSource={logs}
          expandable={{
            expandedRowRender: (record: TReportingLog) => (
              <LogItemTable record={record} />
            ),
            rowExpandable: (record: TReportingLog) => record.logs.length > 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              <RenderIcon
                expanded={expanded}
                onExpand={onExpand}
                record={record}
              />
            ),
          }}
          pagination={pagination}
        />
      </div>
    </DefaultContentLayout>
  );
};

export default ReportingLogs;

const columns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Start time',
    dataIndex: 'startTime',
    key: 'startTime',
    render: (startTime: string) =>
      dayjs(startTime).format('YYYY-MM-DD HH:mm:ss'),
  },
  {
    title: 'End time',
    dataIndex: 'endTime',
    key: 'endTime',
    render: (endTime: string) => dayjs(endTime).format('YYYY-MM-DD HH:mm:ss'),
  },
  {
    title: 'Total time',
    key: 'totalTime',
    render: (_, { startTime, endTime }) =>
      dayjs(endTime).diff(dayjs(startTime), 'milliseconds') / 1000 + 's',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: string) => {
      switch (status) {
        case EReportingStatus.SUCCESS:
          return <Tag color="green">{status}</Tag>;
        case EReportingStatus.FAILED:
          return <Tag color="red">{status}</Tag>;
        case EReportingStatus.IN_PROGRESS:
          return <Tag color="blue">{status}</Tag>;
        case EReportingStatus.QUEUED:
          return <Tag color="gray">{status}</Tag>;
        default:
          return status;
      }
    },
  },
];
